import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Popover, Slider, Tab, Tabs, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import { GRADIENT_PRESETS, SOLID_SWATCHES, isGradient, isTransparent } from '../reelCanvasStyles';

export interface ColorFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    /** Offer a "No colour" (transparent) option. */
    allowTransparent?: boolean;
    /** Offer gradient presets + a two-stop gradient builder. */
    allowGradient?: boolean;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    disabled?: boolean;
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Best-effort hex for the native colour input (falls back to white). */
const toHexForInput = (value: string): string => {
    if (HEX_RE.test(value)) {
        if (value.length === 4) return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
        return value;
    }
    const m = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (m) {
        const h = (n: string) => Number(n).toString(16).padStart(2, '0');
        return `#${h(m[1])}${h(m[2])}${h(m[3])}`;
    }
    return '#ffffff';
};

const hexToRgba = (hex: string, alphaPct: number): string => {
    const h = toHexForInput(hex).slice(1);
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${Math.round(alphaPct) / 100})`;
};

const alphaOf = (value: string): number => {
    const m = value.match(/rgba\([^)]*,\s*([\d.]+)\s*\)/i);
    return m ? Math.round(Number(m[1]) * 100) : 100;
};

/**
 * Colour picker: swatch button + popover with Solid (native picker, alpha, swatches, free text)
 * and optional Gradient (presets + two-colour builder) tabs, plus a "No colour" toggle.
 */
export const ColorField: React.FC<ColorFieldProps> = ({
    label,
    value,
    onChange,
    allowTransparent = false,
    allowGradient = false,
    size = 'small',
    fullWidth = false,
    disabled = false,
}) => {
    const theme = useTheme();
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<'solid' | 'gradient'>(isGradient(value) ? 'gradient' : 'solid');
    const [text, setText] = useState(value);
    const [gradA, setGradA] = useState('#ff7e5f');
    const [gradB, setGradB] = useState('#feb47b');
    const [gradAngle, setGradAngle] = useState(135);

    useEffect(() => {
        setText(value);
        if (isGradient(value)) setTab('gradient');
    }, [value]);

    const transparent = isTransparent(value);
    const gradient = isGradient(value);
    const swatchBackground = useMemo(() => {
        if (transparent) {
            return 'repeating-conic-gradient(rgba(0,0,0,0.25) 0% 25%, rgba(255,255,255,0.35) 0% 50%) 50% / 10px 10px';
        }
        return value;
    }, [value, transparent]);

    const commitText = () => {
        const t = text.trim();
        if (!t) return;
        onChange(t);
    };

    const applyGradient = (a = gradA, b = gradB, angle = gradAngle) => onChange(`linear-gradient(${angle}deg, ${a} 0%, ${b} 100%)`);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: fullWidth ? '100%' : 140 }}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Tooltip title={transparent ? 'No colour' : value}>
                    <Button
                        ref={anchorRef}
                        variant="outlined"
                        size={size}
                        disabled={disabled}
                        onClick={() => setOpen(true)}
                        aria-label={`${label}: ${transparent ? 'no colour' : value}`}
                        sx={{
                            minWidth: 0,
                            width: 40,
                            height: 32,
                            p: 0,
                            borderColor: 'divider',
                            background: swatchBackground,
                            '&:hover': { background: swatchBackground, borderColor: theme.palette.secondary.main },
                        }}
                    />
                </Tooltip>
                <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }} title={value}>
                    {transparent ? 'No colour' : gradient ? 'Gradient' : value}
                </Typography>
                {allowTransparent && (
                    <Tooltip title="No colour (transparent)">
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                aria-label={`${label}: no colour`}
                                onClick={() => onChange('transparent')}
                                sx={{ color: transparent ? theme.palette.secondary.main : 'text.secondary' }}
                            >
                                <BlockIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { p: 1.5, width: 300 } }}
            >
                {allowGradient && (
                    <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="fullWidth" sx={{ mb: 1, minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0 } }}>
                        <Tab label="Solid" value="solid" />
                        <Tab label="Gradient" value="gradient" />
                    </Tabs>
                )}

                {tab === 'solid' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                component="input"
                                type="color"
                                aria-label={`${label} colour picker`}
                                value={toHexForInput(transparent || gradient ? '#ffffff' : value)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const a = alphaOf(value);
                                    onChange(a < 100 ? hexToRgba(e.target.value, a) : e.target.value);
                                }}
                                sx={{ width: 44, height: 36, p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                            />
                            <TextField
                                size="small"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onBlur={commitText}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitText();
                                }}
                                placeholder="#rrggbb, rgba(), hsl()…"
                                fullWidth
                                inputProps={{ 'aria-label': `${label} value` }}
                            />
                        </Box>
                        {!transparent && !gradient && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Opacity {alphaOf(value)}%
                                </Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={100}
                                    value={alphaOf(value)}
                                    onChange={(_e, v) => onChange(hexToRgba(value, v as number))}
                                    aria-label={`${label} opacity`}
                                />
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {SOLID_SWATCHES.map((c) => (
                                <Box
                                    key={c}
                                    role="button"
                                    aria-label={`Use ${c}`}
                                    onClick={() => onChange(c)}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '6px',
                                        backgroundColor: c,
                                        border: `1px solid ${alpha(theme.palette.text.primary, 0.25)}`,
                                        cursor: 'pointer',
                                        outline: value.toLowerCase() === c ? `2px solid ${theme.palette.secondary.main}` : 'none',
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {tab === 'gradient' && allowGradient && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {GRADIENT_PRESETS.map((g) => (
                                <Tooltip key={g.label} title={g.label}>
                                    <Box
                                        role="button"
                                        aria-label={`Use gradient ${g.label}`}
                                        onClick={() => onChange(g.value)}
                                        sx={{
                                            width: 40,
                                            height: 28,
                                            borderRadius: '6px',
                                            backgroundImage: g.value,
                                            border: `1px solid ${alpha(theme.palette.text.primary, 0.25)}`,
                                            cursor: 'pointer',
                                            outline: value === g.value ? `2px solid ${theme.palette.secondary.main}` : 'none',
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                            Custom two-colour gradient
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                component="input"
                                type="color"
                                aria-label="Gradient start colour"
                                value={gradA}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setGradA(e.target.value);
                                    applyGradient(e.target.value, gradB, gradAngle);
                                }}
                                sx={{ width: 40, height: 32, p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                            />
                            <Box
                                component="input"
                                type="color"
                                aria-label="Gradient end colour"
                                value={gradB}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setGradB(e.target.value);
                                    applyGradient(gradA, e.target.value, gradAngle);
                                }}
                                sx={{ width: 40, height: 32, p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Angle {gradAngle}°
                                </Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={360}
                                    value={gradAngle}
                                    onChange={(_e, v) => {
                                        setGradAngle(v as number);
                                        applyGradient(gradA, gradB, v as number);
                                    }}
                                    aria-label="Gradient angle"
                                />
                            </Box>
                        </Box>
                        <Box sx={{ height: 28, borderRadius: 1, backgroundImage: `linear-gradient(${gradAngle}deg, ${gradA} 0%, ${gradB} 100%)` }} />
                    </Box>
                )}
            </Popover>
        </Box>
    );
};

export default ColorField;
